define(['lodash', 'utils', 'coreUtils'], function (_, utils, coreUtils) {
    'use strict';

    const privates = new coreUtils.SiteDataPrivates(); //eslint-disable-line santa/no-module-state

    const ENABLED_ACTIONS_WHEN_COMP_IS_HIDDEN = ['viewportLeave', 'viewportEnter', 'fetchData'];

    function getStyleId(styleId, skin) {
        if (_.isString(styleId) && !_.isEmpty(styleId)) {
            return styleId;
        }

        return utils.santaTypes.shortenStyleId(skin);
    }

    /**
     * @private
     * @param {data.compStructure} structure
     * @param {core.SiteData} siteData
     * @return {string} the skin name of the comp
     */
    function getSkin(styleId, skin, getThemeData) {
        const themeData = styleId && getThemeData(styleId);
        return themeData ? themeData.skin : skin;
    }

    function getCompProp(siteAPI, propertyQuery, pageId) {
        const siteData = siteAPI.getSiteData();
        return siteData.getDataByQuery(propertyQuery, pageId, siteData.dataTypes.PROPERTIES) || {};
    }

    function getScreenLayoutFunc(siteData) {
        if (privates.has(siteData)) {
            return privates.get(siteData);
        }

        const getScreenLayout = function () {
            return {
                pageMargins: siteData.getPageMargins(),
                screenWidth: siteData.getScreenWidth(),
                siteWidth: siteData.getSiteWidth(),
                siteX: siteData.getSiteX(),
                screenHeight: siteData.getScreenHeight()
            };
        };
        privates.set(siteData, getScreenLayout);

        return getScreenLayout;
    }

    /**
     * @private
     * @param {data.compStructure} structure
     * @return {comp.compLayout}
     */
    function getStyle(layout, siteAPI, compId) {
        const siteData = siteAPI.getSiteData();
        const style = coreUtils.layoutUtils.getStyle(layout, getScreenLayoutFunc(siteData), siteAPI.getLayoutMechanism());
        if (compId === siteData.MASTER_PAGE_ID) {
            style.position = siteAPI.getSiteRootPosition();
        }

        return style;
    }

    function duplicateWixCodeBehavior(compId, behavior) {
        const behaviorName = _.get(behavior, 'behavior.name');
        if (behaviorName !== 'runCode' && behaviorName !== 'runAppCode') {
            return behavior;
        }
        const duplicateBehavior = _.cloneDeep(behavior);
        _.set(duplicateBehavior, 'action.sourceId', compId);


        if (_.get(duplicateBehavior, 'behavior.params.compId')) {
            _.set(duplicateBehavior, 'behavior.params.compId', compId);
        }
        return duplicateBehavior;
    }

    function getCompBehaviors(siteAPI, propertyQuery, behaviorQuery, pageId, compId) {
        const siteData = siteAPI.getSiteData();
        const behaviorsDataItem = siteData.getDataByQuery(behaviorQuery, pageId, siteData.dataTypes.BEHAVIORS) || {};

        const isHidden = _.get(getCompProp(siteAPI, propertyQuery, pageId), 'isHidden');
        if (isHidden) {
            return _.filter(behaviorsDataItem.items, actionAndBehaviorObj => _.includes(ENABLED_ACTIONS_WHEN_COMP_IS_HIDDEN, actionAndBehaviorObj.action.name));
        }

        if (coreUtils.displayedOnlyStructureUtil.isDisplayedComponent(compId)) {
            behaviorsDataItem.items = _.map(behaviorsDataItem.items, behavior => duplicateWixCodeBehavior(compId, behavior));
        }

        return behaviorsDataItem.items || [];
    }

    function getCompActions(siteAPI, propertyQuery, behaviorQuery, compId, rootId) {
        const behaviorsAspect = siteAPI.getSiteAspect('behaviorsAspect');
        const compBehaviors = getCompBehaviors(siteAPI, propertyQuery, behaviorQuery, rootId, compId);
        const compActionsBehaviors = behaviorsAspect.convertBehaviors(compBehaviors, compId);
        return behaviorsAspect.getCompActions('comp', compId, rootId, compActionsBehaviors);
    }

    function getCompData(siteAPI, dataQuery, pageId) {
        const siteData = siteAPI.getSiteData();
        return siteData.getDataByQuery(dataQuery, pageId, siteData.dataTypes.DATA);
    }

    function getCompDesign(siteAPI, designQuery, pageId) {
        const siteData = siteAPI.getSiteData();
        return siteData.getDataByQuery(designQuery, pageId, siteData.dataTypes.DESIGN);
    }

    function getRootProps(compClass, rootId, siteAPI) {
        const siteData = siteAPI.getSiteData();

        const displayedDAL = siteAPI.getDisplayedDAL();
        const pointers = siteAPI.getPointers();
        const pagePointer = pointers.components.getPage(rootId, siteData.getViewMode());
        const currentPageComponentType = displayedDAL.get(pointers.getInnerPointer(pagePointer, ['componentType'])) || 'mobile.core.components.MasterPage';
        const dataQuery = displayedDAL.get(pointers.getInnerPointer(pagePointer, ['dataQuery']));
        const compData = getCompData(siteAPI, dataQuery, rootId);
        const currentLang = siteData.getCurrentLanguageCode();
        const langPostfix = currentLang ? `_${currentLang}` : '';

        return {
            rootNavigationInfo: siteData.getExistingRootNavigationInfo(rootId),
            componentType: currentPageComponentType,
            siteData,
            siteAPI,
            id: rootId,
            key: `${rootId}_${siteData.getViewMode()}${langPostfix}`,
            ref: rootId,
            compData,
            rootId
        };
    }

    return {
        getRootProps,
        getStyleId,
        getSkin,
        getCompProp,
        getStyle,
        getCompData,
        getCompDesign,
        getCompBehaviors,
        getCompActions
    };
});
